import { type DriveFile } from '@/shared/DriveTypes';
import { ChevronRight, Home, MoreHoriz } from '@mui/icons-material';
import { IconButton, Menu, MenuItem } from '@mui/material';
import cx from 'classnames';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { Fragment } from 'react';
import { Link } from 'wouter';
import { hidden, hideMobile } from '~/app.css';
import { CopyPage } from '~/elements/CopyPage/CopyPage';
import { darkTheme, lightTheme } from '~/theme.css';
import { LEGACY } from '~/utils/constants';
import { isMarkdownLink } from '~/utils/isMarkdownLink';
import { useTheme } from '~/utils/store';
import { getId, hasAPublicPermission, legacyClick } from '~/utils/utils';
import * as styles from './Breadcrumbs.css';

const getBreadcrumbs = (file: DriveFile, wiki: DriveFile, files: DriveFile[]): DriveFile[] => {
  if (file.id === wiki.id) return [wiki];
  if (file.parents?.[0] === wiki.id) return [wiki, file];
  const parent = files.find((o) => getId(o) === file.parents?.[0]);
  if (!parent) return [];
  return [...getBreadcrumbs(parent, wiki, files), file];
};

export const Empty = () => (
  <div className={cx([styles.breadcrumbs, hidden, hideMobile])}>
    <div className={styles.breadcrumb}>
      <div className={styles.pageLabel} aria-label="breadcrumb">
        &nbsp;
      </div>
    </div>
  </div>
);

const Ellipsis = ({ breadcrumbs, wiki }: { breadcrumbs: DriveFile[]; wiki: DriveFile }) => {
  const menu = usePopupState({ variant: 'popover' });
  const theme = useTheme();
  return (
    <>
      <Menu
        {...bindMenu(menu)}
        className={cx({
          [darkTheme]: theme === 'dark',
          [lightTheme]: theme === 'light',
        })}
        slotProps={{
          paper: {
            className: styles.menu,
          },
        }}
      >
        {breadcrumbs.map((o) => (
          <MenuItem
            key={o.id}
            to={LEGACY ? `/app/page/${o.id}?p=${wiki.id}` : `/app/page/${wiki.id}/${o.id}`}
            component={Link}
            onClick={legacyClick}
            className={styles.menuItem}
          >
            {o.name}
          </MenuItem>
        ))}
      </Menu>
      <IconButton
        {...bindTrigger(menu)}
        size="small"
        style={{ margin: -5 }}
        className={cx(styles.breadcrumb, styles.ellipsisButton)}
        disableTouchRipple
        aria-label="breadcrumb"
      >
        <MoreHoriz fontSize="small" />
      </IconButton>
      <span className={styles.chevron}>
        <ChevronRight />
      </span>
    </>
  );
};

export const Breadcrumbs = ({
  files,
  file,
  wiki,
  markdown,
}: {
  files: DriveFile[];
  file: DriveFile;
  wiki: DriveFile;
  markdown?: string | undefined;
}) => {
  const allBreadcrumbs = getBreadcrumbs(file, wiki, files);
  const home = allBreadcrumbs.at(0);
  if (file.id === wiki.id || !home) return <Empty />;

  const breadcrumbs = allBreadcrumbs.length > 3 ? [home].concat(allBreadcrumbs.slice(1).slice(-2)) : allBreadcrumbs;
  const ids = new Set(breadcrumbs.map((o) => o.id));

  return (
    <div className={styles.breadcrumbs}>
      {breadcrumbs
        .map((o) => {
          const markdown = isMarkdownLink(o);
          return markdown
            ? {
                ...o,
                name: markdown.name,
              }
            : o;
        })
        .map((item, i) => {
          const legacyTo = LEGACY ? `/app/page/${item.id}?p=${wiki.id}` : '';
          const to = LEGACY ? legacyTo : `/app/page/${wiki.id}/${item.id}`;
          const isLast = i === breadcrumbs.length - 1;
          const id = isLast ? `${item.name}-last` : item.id;
          return (
            <Fragment key={id}>
              {i === 1 && breadcrumbs.length !== allBreadcrumbs.length && (
                <Ellipsis breadcrumbs={allBreadcrumbs.filter((b) => !ids.has(b.id))} wiki={wiki} />
              )}

              {/* Home or parent link */}
              {i !== breadcrumbs.length - 1 && (
                <Link className={styles.breadcrumb} to={to} onClick={legacyClick} aria-label="breadcrumb">
                  {i === 0 ? <Home className={styles.homeIcon} /> : <span>{item.name}</span>}
                </Link>
              )}

              {/* Chevrons */}
              {i !== breadcrumbs.length - 1 && (
                <span className={styles.chevron}>
                  <ChevronRight />
                </span>
              )}

              {/* Page name */}
              {i === breadcrumbs.length - 1 && (
                <Link to={to} className={styles.pageLabel} aria-label="breadcrumb">
                  {item.name}
                </Link>
              )}
            </Fragment>
          );
        })}
      {markdown && (
        <CopyPage
          markdown={markdown}
          className={styles.copyPage}
          fileId={file.id}
          isPublic={hasAPublicPermission(wiki)}
        />
      )}
    </div>
  );
};

export const EmptyBreadCrumbs = ({ style }: { style?: React.CSSProperties }) => (
  <div className={cx(styles.breadcrumbs, hidden, hideMobile)} style={style}>
    <span className={styles.pageLabel}>a</span>
  </div>
);
